/**
 * Global header "At Delivery Center" tile — count + amount + oldest-order-age
 * for orders currently sitting at the delivery hub (At Delivery Center /
 * Undelivered / Out For Delivery), same definition as the "All Orders" tab on
 * Bulk Analysis > Delivery Center Orders. Click-through lands there directly.
 *
 * Business reason (Shailendra, 2026-09-23): these orders are RTO risk — the
 * buyer needs a heads-up call ("your order has reached the hub, please be
 * ready to collect it") before it bounces back. Agents get a blinking red
 * dot next to the oldest-on-hub number so it can't be missed — but only when
 * they actually have an order sitting on the hub (no dot at zero).
 *
 * RBAC (Shailendra, 2026-09-24): hidden from plain admins now — visible only
 * to Manjay (bulkWaManager flag), Ameen (role 17), and roles 5/13/14.
 *   5        → own orders only.
 *   13       → two blocks: own + team.
 *   14/17/Manjay → one combined block for the whole downline.
 */
import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@material-ui/core';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import { getDecryptDataFromLocal } from '../../../utils/encrypt';

const ALLOWED_ROLES = [5, 13, 14, 17];
const isBulkWaManager = (() => {
  try {
    return localStorage.getItem('bulkWaManager') === 'true';
  } catch (e) {
    return false;
  }
})();
const POLL_MS = 600000; // 10 min (Shailendra 2026-09-24) — was 60s, this doesn't need minute-level freshness

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

export default function DeliveryCenterHubTile() {
  const role = Number(getDecryptDataFromLocal('setRole'));
  const canView = ALLOWED_ROLES.includes(role) || isBulkWaManager;
  const history = useHistory();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!canView) return undefined;
    let cancelled = false;

    const fetchSummary = () => {
      axios
        .post(`${process.env.REACT_APP_PATH_BULK}/sales/getDeliveryCenterHubSummary`, {})
        .then((res) => {
          if (!cancelled && res.data?.success) setData(res.data.data);
        })
        .catch(() => {});
    };

    fetchSummary();
    const interval = setInterval(fetchSummary, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [canView]);

  if (!canView || !data) return null;

  const handleClick = () => history.push('/bulk/delivery-center-orders');
  const isAgent = role === 5;

  const StatCol = ({ value, label, dot }) => (
    <Box sx={{ textAlign: 'center', px: { xs: 1, md: 2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}>
        <Typography sx={{ fontSize: { xs: '1.15rem', md: '1.45rem' }, fontWeight: 900, color: 'inherit', whiteSpace: 'nowrap' }}>
          {value}
        </Typography>
        {dot && (
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: '#d32f2f',
              animation: 'dcHubDotBlink 1s ease-in-out infinite',
              '@keyframes dcHubDotBlink': {
                '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                '50%': { opacity: 0.25, transform: 'scale(0.7)' },
              },
            }}
          />
        )}
      </Box>
      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: 'inherit', opacity: 0.85, whiteSpace: 'nowrap' }}>
        {label}
      </Typography>
    </Box>
  );

  const Divider = () => <Box sx={{ width: '2px', alignSelf: 'stretch', backgroundColor: 'rgba(211,47,47,0.35)' }} />;

  const Block = ({ summary, title }) => {
    const hasOldest = summary.oldestAgeingDays !== null && summary.oldestAgeingDays !== undefined;
    const showDot = isAgent && hasOldest && summary.count > 0;
    return (
      <Box sx={{ textAlign: 'center' }}>
        {title && (
          <Typography sx={{ fontSize: '0.66rem', fontWeight: 800, color: 'inherit', mb: 0.25, letterSpacing: '0.03em' }}>
            {title}
          </Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <StatCol value={summary.count} label="orders" />
          <Divider />
          <StatCol value={inr(summary.amount)} label="amount" />
          <Divider />
          <StatCol value={hasOldest ? `${summary.oldestAgeingDays}d` : '—'} label="oldest on hub" dot={showDot} />
        </Box>
      </Box>
    );
  };

  return (
    <Box
      onClick={handleClick}
      title="At Delivery Center / Undelivered / Out For Delivery — click to view"
      sx={{
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        px: 3,
        py: 0.75,
        borderRadius: '12px',
        border: '3px solid #d32f2f',
        color: '#d32f2f',
        minWidth: { xs: 300, sm: 460, md: data.scope === 'split' ? 640 : 460 },
        maxWidth: 760,
      }}
    >
      {data.scope === 'split' ? (
        <>
          <Block summary={data.own} title="AT HUB — YOU" />
          <Divider />
          <Block summary={data.team} title="AT HUB — TEAM" />
        </>
      ) : (
        <Block summary={data.summary} title={null} />
      )}
    </Box>
  );
}
